// src/config/permissions.js
// Path-based access control configuration

// Default allowed paths (fallback when backend is unavailable)
const defaultAllowedPaths = [
  '/home',
  '/home/cultural',
  '/home/cultural/ich',
  '/home/cultural/ich/heritor',
  '/home/cultural/ich/project',
  '/home/cultural/ich/base',
  '/home/cultural/ich/achievement',
  '/home/cultural/ich/innovation',
  '/home/cultural/ich/news',
  '/home/cultural/ich/updates',
  '/home/cultural/ich/assessment',
  '/home/cultural/tangible',
  '/home/cultural/tangible/immovable',
  '/home/cultural/tangible/village',
  '/home/cultural/ecology',
  '/home/cultural/museums',
  '/home/cultural/relics',
  '/home/cultural/environment',
  '/home/cultural/documents',
  '/home/cultural/logs',
  '/home/cultural/api',
  '/home/ai',
  '/home/ai/collection',
  '/home/ai/monitor',
  '/home/ai/analysis',
  '/home/ai/analysis/monitoring',
  '/home/ai/analysis/training',
  '/home/ai/analysis/activity',
  '/home/ai/analysis/vitality',
  '/home/ai/analysis/personnel',
  '/home/ai/analysis/ecosystem',
  '/home/ai/analysis/qgzc',
  '/home/ai/analysis/cyyc',
  '/home/ai/search',
  '/home/ai/risk',
  '/home/ai/decision',
  '/home/ai/management',
  '/home/service',
  // '/home/service/audit',
  '/home/service/evaluation',
  '/home/service/assessment',
  '/home/service/approval',
  '/home/service/notification',
  '/home/service/news',
  '/home/service/updates',
  '/home/service/policy',
  '/home/service/reservation',
  '/home/ai/news-consultation',
  '/home/service/guide',
  '/home/service/risk',
  '/home/service/community',
  // 新增的服务管家子页面
  '/home/service/risk-indicator',
  '/home/service/risk-warnings',
  '/home/service/risk-plans',
  '/home/service/enterprise-needs',
  '/home/service/info-collection',
  '/home/service/suggestions',
  '/home/service/community-posts',
  '/home/service/community-comments',
  '/home/service/community-users',
  '/home/service/points',
  '/home/service/points-rule',
  '/home/service/feedback',
  '/home/service/role-management',
  '/home/service/user-management',
  '/home/service/organization',
  '/home/service/approval-flow',
  '/home/service/audit-task',
  '/home/service/audit-opinion',
  '/home/service/public-notice',
  '/home/ecosystem',
  '/home/ecosystem/industry',
  '/home/ecosystem/education',
  '/home/ecosystem/tourism',
  '/home/ecosystem/tourism/experience',
  '/home/ecosystem/tourism/half-day',
  '/home/ecosystem/tourism/one-day',
  '/home/ecosystem/tourism/three-day',
  '/home/ecosystem/tourism/ceramics',
  '/home/ecosystem/tourism/visit',
  '/home/ecosystem/technology',
  '/home/ecosystem/exchange'
];

// Get allowed paths from store or use default
export function getAllowedPaths(store) {
  const backendPaths = store?.getters?.allowedPaths;
  return backendPaths || defaultAllowedPaths;
}

// Helper function to check if user has permission to access a path
export function hasPathPermission(path, store = null) {
  const allowedPaths = getAllowedPaths(store);
  
  // 如果权限列表为空或null，使用默认配置
  if (!allowedPaths || allowedPaths.length === 0) {
    console.log('权限列表为空，使用默认配置');
    return defaultAllowedPaths.includes(path);
  }
  
  // 检查精确匹配
  if (allowedPaths.includes(path)) {
    return true;
  }
  
  // 检查是否有父路径权限（例如：有/home/cultural权限，就可以访问/home/cultural/ich）
  const pathSegments = path.split('/').filter(segment => segment);
  for (let i = pathSegments.length; i > 0; i--) {
    const parentPath = '/' + pathSegments.slice(0, i).join('/');
    if (allowedPaths.includes(parentPath)) {
      return true;
    }
  }
  
  return false;
}

// Helper function to check if user has permission to access any path that starts with the given prefix
export function hasPathPrefixPermission(pathPrefix, store = null) {
  const allowedPaths = getAllowedPaths(store);
  // 检查给定路径是否以allowedPaths中的任何路径开头，或者allowedPaths中是否有路径以给定路径开头
  return allowedPaths.some(path => path.startsWith(pathPrefix) || pathPrefix.startsWith(path));
}

// Helper function to get allowed modules based on path permissions
export function getAllowedModules(store = null) {
  const allowedPaths = getAllowedPaths(store);
  const modules = [];
  
  // Check main modules
  const mainModules = [
    { id: 'cultural', path: '/home/cultural' },
    { id: 'ai', path: '/home/ai' },
    { id: 'service', path: '/home/service' },
    { id: 'ecosystem', path: '/home/ecosystem' }
  ];
  
  mainModules.forEach(module => {
    if (hasPathPrefixPermission(module.path, store)) {
      modules.push(module.id);
    }
  });
  
  return modules;
}

// Helper function to get allowed sub-menus for a module based on path permissions
export function getAllowedSubMenus(module, store = null) {
  const allowedPaths = getAllowedPaths(store);
  const subMenus = [];
  
  
  // Define sub-menu paths for each module
  const subMenuPaths = {
    cultural: [
      { id: 'ich', path: '/home/cultural/ich' },
      { id: 'tangible', path: '/home/cultural/tangible' },
      { id: 'immovable', path: '/home/cultural/tangible/immovable' },
      { id: 'village', path: '/home/cultural/tangible/village' },
      { id: 'ecology', path: '/home/cultural/ecology' },
      { id: 'museums', path: '/home/cultural/museums' },
      { id: 'relics', path: '/home/cultural/relics' },
      { id: 'environment', path: '/home/cultural/environment' },
      { id: 'documents', path: '/home/cultural/documents' },
      { id: 'logs', path: '/home/cultural/logs' },
      { id: 'api', path: '/home/cultural/api' }
    ],
    ai: [
      { id: 'monitoring', path: '/home/ai/analysis/monitoring' },
      { id: 'training', path: '/home/ai/analysis/training' },
      { id: 'activity', path: '/home/ai/analysis/activity' },
      { id: 'vitality', path: '/home/ai/analysis/vitality' },
      { id: 'personnel', path: '/home/ai/analysis/personnel' },
      { id: 'ecosystem', path: '/home/ai/analysis/ecosystem' },
      { id: 'qgzc', path: '/home/ai/analysis/qgzc' },
      { id: 'cyyc', path: '/home/ai/analysis/cyyc' },
      { id: 'search', path: '/home/ai/search' },
      { id: 'news-consultation', path: '/home/ai/news-consultation' }
    ],
    service: [
      { id: 'risk', path: '/home/service/risk' },
      { id: 'community', path: '/home/service/community' },
      // { id: 'audit', path: '/home/service/audit' },
      { id: 'evaluation', path: '/home/service/evaluation' },
      { id: 'assessment', path: '/home/service/assessment' },
      { id: 'notification', path: '/home/service/notification' },
      { id: 'approval', path: '/home/service/approval' },
      { id: 'reservation', path: '/home/service/reservation' },
      { id: 'news', path: '/home/service/news' },
      { id: 'policy', path: '/home/service/policy' },
      { id: 'guide', path: '/home/service/guide' },
      // 新增子菜单映射
      { id: 'risk-indicator', path: '/home/service/risk-indicator' },
      { id: 'risk-warnings', path: '/home/service/risk-warnings' },
      { id: 'risk-plans', path: '/home/service/risk-plans' },
      { id: 'enterprise-needs', path: '/home/service/enterprise-needs' },
      { id: 'info-collection', path: '/home/service/info-collection' },
      { id: 'suggestions', path: '/home/service/suggestions' },
      { id: 'community-posts', path: '/home/service/community-posts' },
      { id: 'community-comments', path: '/home/service/community-comments' },
      { id: 'community-users', path: '/home/service/community-users' },
      { id: 'points', path: '/home/service/points' },
      { id: 'points-rule', path: '/home/service/points-rule' },
      { id: 'feedback', path: '/home/service/feedback' },
      { id: 'role-management', path: '/home/service/role-management' },
      { id: 'user-management', path: '/home/service/user-management' },
      { id: 'organization', path: '/home/service/organization' },
      { id: 'approval-flow', path: '/home/service/approval-flow' },
      { id: 'audit-task', path: '/home/service/audit-task' },
      { id: 'audit-opinion', path: '/home/service/audit-opinion' },
      { id: 'public-notice', path: '/home/service/public-notice' }
    ],
    ecosystem: [
      { id: 'industry', path: '/home/ecosystem/industry' },
      { id: 'education', path: '/home/ecosystem/education' },
      { id: 'tourism', path: '/home/ecosystem/tourism' },
      { id: 'technology', path: '/home/ecosystem/technology' },
      { id: 'exchange', path: '/home/ecosystem/exchange' }
    ]
  };
  
  const moduleSubMenus = subMenuPaths[module] || [];
  moduleSubMenus.forEach(subMenu => {
    const hasPermission = hasPathPrefixPermission(subMenu.path, store);
    if (hasPermission) {
      subMenus.push(subMenu.id);
    }
  });
  
  return subMenus;
}

